import { DestroyRef, Injectable, Signal, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ISvcChipOption } from '@solvace/component/svc-controls';
import { SitesService, UserService } from 'projects/lib-shared-core/src/public-api';
import { catchError, filter, forkJoin, of, switchMap, take, tap } from 'rxjs';

import { SvcActionPlanEscalateApiService } from '../../services/svc-action-plan-escalate-api.service';
import { SvcActionPlanEscalateSystemByApplication } from '../../interfaces/svc-modal-action-plan.interface';
import type { SvcActionPlanEscalateModalData } from './svc-action-plan-escalate-modal.component';

@Injectable()
export class SvcActionPlanEscalateStore {
  readonly #api = inject(SvcActionPlanEscalateApiService);
  readonly #sitesService = inject(SitesService);
  readonly #userService = inject(UserService);
  readonly #destroyRef = inject(DestroyRef);

  #applicationId = '';
  #prefillRoomId: number | null = null;
  #prefillBoardId: number | null = null;

  readonly #sites = signal<ISvcChipOption[]>([]);
  readonly #selectedSiteId = signal<number | null>(null);
  readonly #selectedRoomId = signal<number | null>(null);
  readonly #lockedSystem = signal<SvcActionPlanEscalateSystemByApplication | null>(null);
  readonly #rooms = signal<ISvcChipOption[]>([]);
  readonly #boards = signal<ISvcChipOption[]>([]);
  readonly #isLoadingSiteData = signal<boolean>(false);
  readonly #isLoadingBoards = signal<boolean>(false);
  readonly #prefilledBoardId = signal<number | null>(null);
  readonly #usersSiteId = signal<number | null>(null);
  readonly #users = signal<ISvcChipOption[]>([]);
  readonly #isLoadingUsers = signal<boolean>(false);

  public readonly sites: Signal<ISvcChipOption[]> = this.#sites.asReadonly();
  public readonly selectedSiteId: Signal<number | null> = this.#selectedSiteId.asReadonly();
  public readonly selectedRoomId: Signal<number | null> = this.#selectedRoomId.asReadonly();
  public readonly lockedSystem: Signal<SvcActionPlanEscalateSystemByApplication | null> = this.#lockedSystem.asReadonly();
  public readonly rooms: Signal<ISvcChipOption[]> = this.#rooms.asReadonly();
  public readonly boards: Signal<ISvcChipOption[]> = this.#boards.asReadonly();
  public readonly isLoadingSiteData: Signal<boolean> = this.#isLoadingSiteData.asReadonly();
  public readonly isLoadingBoards: Signal<boolean> = this.#isLoadingBoards.asReadonly();
  public readonly prefilledBoardId: Signal<number | null> = this.#prefilledBoardId.asReadonly();
  public readonly users: Signal<ISvcChipOption[]> = this.#users.asReadonly();
  public readonly isLoadingUsers: Signal<boolean> = this.#isLoadingUsers.asReadonly();

  public readonly lockedSystemName: Signal<string> = computed(() => this.#lockedSystem()?.systemName ?? '');
  public readonly isRoomDisabled: Signal<boolean> = computed(() => !this.#isValidId(this.#selectedSiteId()) || this.#isLoadingSiteData());
  public readonly isBoardDisabled: Signal<boolean> = computed(() => !this.#isValidId(this.#selectedRoomId()) || this.#isLoadingBoards());

  constructor() {
    this.#watchSiteChanges();
    this.#watchRoomChanges();
    this.#watchUsersSiteChanges();
  }

  public init(data: SvcActionPlanEscalateModalData): void {
    this.#applicationId = data?.applicationId ?? '';
    this.#prefillRoomId = this.#normalizeId(data?.planRoomId);
    this.#prefillBoardId = this.#normalizeId(data?.planBoardId);

    this.#sitesService
      .getAll()
      .pipe(take(1), takeUntilDestroyed(this.#destroyRef))
      .subscribe((sites) => {
        this.#sites.set((sites ?? []).map((site) => ({ label: site.siteName, value: site.siteId })));

        if (!this.#isValidId(this.#selectedSiteId())) {
          const currentSiteId = Number((sites ?? []).find((site) => site.isSiteCurrent)?.siteId ?? Number.NaN);
          if (this.#isValidId(currentSiteId)) this.setSite(currentSiteId);
        }
      });

    this.setSite(this.#resolveInitialSiteId(data));
  }

  /**
   * Regular (non-Obeya) escalate: there is no Site combo, so the "Responsável"
   * list is scoped once to the plan's latest escalated site. No-op for an
   * invalid site (caller keeps the global user list passed via modal data).
   */
  public initUsersForSite(siteId: number | null): void {
    this.#usersSiteId.set(this.#normalizeId(siteId));
  }

  public setSite(siteId: number | null): void {
    this.#selectedRoomId.set(null);
    this.#selectedSiteId.set(siteId);
  }

  public setRoom(roomId: number | null): void {
    this.#selectedRoomId.set(roomId);
  }

  #watchSiteChanges(): void {
    toObservable(this.#selectedSiteId)
      .pipe(
        filter((siteId): siteId is number => this.#isValidId(siteId)),
        tap((siteId) => {
          this.#usersSiteId.set(siteId);
          this.#isLoadingSiteData.set(true);
          this.#lockedSystem.set(null);
          this.#rooms.set([]);
          this.#boards.set([]);
        }),
        switchMap((siteId) =>
          forkJoin({
            systems: this.#api.getSystemByApplication(siteId, this.#applicationId).pipe(catchError(() => of([]))),
            rooms: this.#api.getRoomsWithActionPlanWidget(siteId).pipe(catchError(() => of([]))),
          })
        ),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(({ systems, rooms }) => {
        this.#lockedSystem.set(systems[0] ?? null);
        this.#rooms.set(rooms.map((room) => ({ label: room.roomName, value: room.roomId })));
        this.#isLoadingSiteData.set(false);

        if (this.#prefillRoomId != null && rooms.some((room) => room.roomId === this.#prefillRoomId)) {
          const roomId = this.#prefillRoomId;
          this.#prefillRoomId = null;
          this.#selectedRoomId.set(roomId);
        }
      });
  }

  #watchRoomChanges(): void {
    toObservable(this.#selectedRoomId)
      .pipe(
        tap(() => this.#boards.set([])),
        filter((roomId): roomId is number => this.#isValidId(roomId)),
        tap(() => this.#isLoadingBoards.set(true)),
        switchMap((roomId) => this.#api.getBoards(this.#selectedSiteId() ?? 0, roomId).pipe(catchError(() => of([])))),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe((boards) => {
        this.#boards.set(boards.map((board) => ({ label: board.boardName, value: board.boardId })));
        this.#isLoadingBoards.set(false);

        if (this.#prefillBoardId != null && boards.some((board) => board.boardId === this.#prefillBoardId)) {
          this.#prefilledBoardId.set(this.#prefillBoardId);
          this.#prefillBoardId = null;
        }
      });
  }

  #watchUsersSiteChanges(): void {
    toObservable(this.#usersSiteId)
      .pipe(
        filter((siteId): siteId is number => this.#isValidId(siteId)),
        tap(() => this.#isLoadingUsers.set(true)),
        switchMap((siteId) => this.#userService.getUsers(true, siteId).pipe(catchError(() => of([])))),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe((users) => {
        this.#users.set((users ?? []).map((user) => ({ label: user.name, value: user.userId })));
        this.#isLoadingUsers.set(false);
      });
  }

  #resolveInitialSiteId(data: SvcActionPlanEscalateModalData): number | null {
    const escalatedSiteId = this.#normalizeId(data?.escalatedSiteId);
    if (escalatedSiteId != null) return escalatedSiteId;

    const planSiteId = this.#normalizeId(data?.planSiteId);
    if (planSiteId != null) return planSiteId;

    const currentSiteId = Number(this.#sitesService.currentSite?.siteId ?? 0);
    return this.#isValidId(currentSiteId) ? currentSiteId : null;
  }

  #isValidId(value: number | null): value is number {
    return Number.isFinite(value) && (value ?? 0) > 0;
  }

  #normalizeId(value: number | null | undefined): number | null {
    return this.#isValidId(Number(value ?? 0)) ? Number(value) : null;
  }
}
